import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Typography,
  DatePicker,
  Select,
  Button,
  Table,
  message
} from 'antd';
import {
  FileExcelOutlined,
  ReloadOutlined,
  TeamOutlined,
  MedicineBoxOutlined,
  CalendarOutlined,
  StopOutlined,
  RestOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { supabase } from '../../services/supabase';
import { exportLeaveTypeReport } from '../../utils/exportUtils';
import { Page, PageHeader, StatGrid, StatCard, StatusPill, EmptyState, CardTitle } from '../../components/ui';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const LEAVE_TYPE_OPTIONS = [
  { label: 'All Leave Types', value: 'all' },
  { label: 'Sick Leave', value: 'SICK LEAVE' },
  { label: 'Local Leave', value: 'LOCAL LEAVE' },
  { label: 'Public Holiday', value: 'PH' },
  { label: 'Absent', value: 'ABSENT' },
  { label: 'Day Off', value: 'OFF' }
];

const LEAVE_TYPE_LABELS = {
  'SICK LEAVE': 'Sick Leave',
  'LOCAL LEAVE': 'Local Leave',
  PH: 'Public Holiday',
  ABSENT: 'Absent',
  OFF: 'Day Off'
};

const LEAVE_DAY_KEYS = {
  'SICK LEAVE': 'sickDays',
  'LOCAL LEAVE': 'localLeaveDays',
  PH: 'publicHolidayDays',
  ABSENT: 'absentDays',
  OFF: 'dayOffDays'
};

const LEAVE_HOUR_KEYS = {
  'SICK LEAVE': 'sickHours',
  'LOCAL LEAVE': 'localLeaveHours',
  PH: 'publicHolidayHours',
  ABSENT: 'absentHours',
  OFF: 'dayOffHours'
};

const createEmptySummary = () => ({
  totalLeaveRecords: 0,
  sickDays: 0,
  localLeaveDays: 0,
  publicHolidayDays: 0,
  absentDays: 0,
  dayOffDays: 0,
  totalHours: 0,
  employeesWithLeave: 0
});

const getEmployeeName = (employee) => {
  const firstName = employee?.first_name || '';
  const lastName = employee?.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || 'Unknown Employee';
};

const createEmptyEmployeeRow = (employee) => ({
  key: employee?.id || 'unknown',
  employeeId: employee?.id || 'unknown',
  employeeName: getEmployeeName(employee),
  employeeCode: employee?.employee_number || employee?.fingerprint_id || '',
  department: employee?.department || 'Unassigned',
  sickDays: 0,
  localLeaveDays: 0,
  publicHolidayDays: 0,
  absentDays: 0,
  dayOffDays: 0,
  sickHours: 0,
  localLeaveHours: 0,
  publicHolidayHours: 0,
  absentHours: 0,
  dayOffHours: 0,
  totalLeaveDays: 0,
  totalLeaveHours: 0,
  filteredTypeDays: 0
});

const resolveLeaveType = (record) => {
  if (record?.leave_type) {
    return record.leave_type;
  }
  if (record?.status === 'absent') {
    return 'ABSENT';
  }
  return null;
};

const buildSummary = (records) => {
  const summary = createEmptySummary();
  const employeeSet = new Set();

  records.forEach((record) => {
    const leaveType = record.resolvedLeaveType;
    employeeSet.add(record.employee_id);
    summary.totalLeaveRecords += 1;
    summary.totalHours += record.parsedHours;

    if (leaveType === 'SICK LEAVE') summary.sickDays += 1;
    if (leaveType === 'LOCAL LEAVE') summary.localLeaveDays += 1;
    if (leaveType === 'PH') summary.publicHolidayDays += 1;
    if (leaveType === 'ABSENT') summary.absentDays += 1;
    if (leaveType === 'OFF') summary.dayOffDays += 1;
  });

  summary.totalHours = Number(summary.totalHours.toFixed(2));
  summary.employeesWithLeave = employeeSet.size;
  return summary;
};

const buildReportRows = (records, employees, employeeFilter, leaveTypeFilter) => {
  const rowMap = new Map();
  const baseEmployees =
    employeeFilter === 'all'
      ? employees
      : employees.filter((employee) => employee.id === employeeFilter);

  baseEmployees.forEach((employee) => {
    rowMap.set(employee.id, createEmptyEmployeeRow(employee));
  });

  records.forEach((record) => {
    const employeeId = record.employee_id;
    const employeeInfo = record.employees || {};
    const leaveType = record.resolvedLeaveType;
    const dayKey = LEAVE_DAY_KEYS[leaveType];
    const hourKey = LEAVE_HOUR_KEYS[leaveType];

    if (!rowMap.has(employeeId)) {
      rowMap.set(employeeId, createEmptyEmployeeRow({ id: employeeId, ...employeeInfo }));
    }

    const row = rowMap.get(employeeId);
    if (dayKey) row[dayKey] += 1;
    if (hourKey) row[hourKey] += record.parsedHours;
    row.totalLeaveDays += 1;
    row.totalLeaveHours += record.parsedHours;

    if (leaveTypeFilter !== 'all' && leaveType === leaveTypeFilter) {
      row.filteredTypeDays += 1;
    }
  });

  let rows = Array.from(rowMap.values()).map((row) => ({
    ...row,
    sickHours: Number(row.sickHours.toFixed(2)),
    localLeaveHours: Number(row.localLeaveHours.toFixed(2)),
    publicHolidayHours: Number(row.publicHolidayHours.toFixed(2)),
    absentHours: Number(row.absentHours.toFixed(2)),
    dayOffHours: Number(row.dayOffHours.toFixed(2)),
    totalLeaveHours: Number(row.totalLeaveHours.toFixed(2))
  }));

  const sortKey = leaveTypeFilter === 'all' ? 'totalLeaveDays' : 'filteredTypeDays';

  if (employeeFilter === 'all') {
    rows = rows.filter((row) => row[sortKey] > 0);
  }

  rows.sort((a, b) => {
    const countDiff = b[sortKey] - a[sortKey];
    if (countDiff !== 0) return countDiff;
    return a.employeeName.localeCompare(b.employeeName);
  });

  return rows;
};

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [employees, setEmployees] = useState([]);
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [leaveTypeFilter, setLeaveTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);
  const [reportRows, setReportRows] = useState([]);
  const [reportSummary, setReportSummary] = useState(createEmptySummary());
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchEmployees = useCallback(async (targetCompanyId) => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('id, first_name, last_name, department, employee_number, fingerprint_id')
        .eq('company_id', targetCompanyId)
        .order('first_name', { ascending: true });

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
      message.error('Error fetching employees');
    }
  }, []);

  const fetchCompanyData = useCallback(async () => {
    try {
      const {
        data: { user }
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('No user found');
      }

      const { data, error } = await supabase
        .from('companies')
        .select('id, name')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;

      setCompanyId(data.id);
      setCompanyName(data.name || 'Company');
      fetchEmployees(data.id);
    } catch (error) {
      console.error('Error fetching company:', error);
      message.error('Error fetching company data');
    }
  }, [fetchEmployees]);

  const fetchReportData = useCallback(async (targetCompanyId = companyId) => {
    if (!targetCompanyId || !dateRange || dateRange.length !== 2) return;

    try {
      setLoading(true);
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');

      let query = supabase
        .from('timesheet_daily_records')
        .select(`
          id,
          employee_id,
          date,
          leave_type,
          total_hours,
          status,
          employees:employee_id (
            id,
            first_name,
            last_name,
            department,
            employee_number,
            fingerprint_id
          )
        `)
        .eq('company_id', targetCompanyId)
        .gte('date', startDate)
        .lte('date', endDate)
        .or('leave_type.not.is.null,status.eq.absent')
        .order('date', { ascending: false });

      if (employeeFilter !== 'all') {
        query = query.eq('employee_id', employeeFilter);
      }

      const { data, error } = await query;
      if (error) throw error;

      const normalizedRecords = (data || [])
        .map((record) => ({
          ...record,
          resolvedLeaveType: resolveLeaveType(record),
          parsedHours: Number(record.total_hours || 0)
        }))
        .filter((record) => Boolean(record.resolvedLeaveType));

      const recordsForTable =
        leaveTypeFilter === 'all'
          ? normalizedRecords
          : normalizedRecords.filter((record) => record.resolvedLeaveType === leaveTypeFilter);

      const summary = buildSummary(normalizedRecords);
      const rows = buildReportRows(recordsForTable, employees, employeeFilter, leaveTypeFilter);

      setFilteredRecords(recordsForTable);
      setReportSummary(summary);
      setReportRows(rows);
      setLastUpdated(dayjs());
    } catch (error) {
      console.error('Error fetching leave report:', error);
      message.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  }, [companyId, dateRange, employeeFilter, leaveTypeFilter, employees]);

  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  useEffect(() => {
    if (companyId && dateRange?.length === 2) {
      fetchReportData(companyId);
    }
  }, [companyId, dateRange, fetchReportData]);

  const employeeOptions = useMemo(() => {
    const options = [
      { label: 'All Employees', value: 'all' }
    ];

    employees.forEach((employee) => {
      const fullName = getEmployeeName(employee);
      const employeeCode = employee.employee_number || employee.fingerprint_id;
      options.push({
        value: employee.id,
        label: employeeCode ? `${fullName} (${employeeCode})` : fullName
      });
    });

    return options;
  }, [employees]);

  const selectedEmployeeName = useMemo(() => {
    if (employeeFilter === 'all') return 'All Employees';
    const employee = employees.find((item) => item.id === employeeFilter);
    return employee ? getEmployeeName(employee) : 'Selected Employee';
  }, [employees, employeeFilter]);

  const handleDateRangeChange = (dates) => {
    if (!dates || dates.length !== 2) return;
    setDateRange([dates[0].startOf('day'), dates[1].endOf('day')]);
  };

  const handleExport = () => {
    if (reportRows.length === 0) {
      message.warning('No report data to export');
      return;
    }

    exportLeaveTypeReport({
      rows: reportRows,
      records: filteredRecords,
      summary: reportSummary,
      dateRange,
      companyName,
      leaveTypeFilter,
      employeeName: selectedEmployeeName
    });
  };

  const renderCount = (value, tone) => {
    if (!value) return <span className="num num--muted">0</span>;
    return <StatusPill tone={tone} plain>{value}</StatusPill>;
  };

  const columns = [
    {
      title: 'Employee',
      dataIndex: 'employeeName',
      key: 'employeeName',
      width: 230,
      render: (_, record) => (
        <span className="stack-cell">
          <span style={{ fontWeight: 600 }}>{record.employeeName}</span>
          <span className="stack-cell__sub">{record.employeeCode || 'No employee code'}</span>
        </span>
      )
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      width: 150,
      render: (department) => <span className="chip">{department || 'Unassigned'}</span>
    },
    {
      title: 'Sick',
      dataIndex: 'sickDays',
      key: 'sickDays',
      align: 'center',
      width: 95,
      render: (value) => renderCount(value, 'warning')
    },
    {
      title: 'Local Leave',
      dataIndex: 'localLeaveDays',
      key: 'localLeaveDays',
      align: 'center',
      width: 115,
      render: (value) => renderCount(value, 'primary')
    },
    {
      title: 'Public Holiday',
      dataIndex: 'publicHolidayDays',
      key: 'publicHolidayDays',
      align: 'center',
      width: 120,
      render: (value) => renderCount(value, 'primary')
    },
    {
      title: 'Absent',
      dataIndex: 'absentDays',
      key: 'absentDays',
      align: 'center',
      width: 95,
      render: (value) => renderCount(value, 'danger')
    },
    {
      title: 'Day Off',
      dataIndex: 'dayOffDays',
      key: 'dayOffDays',
      align: 'center',
      width: 95,
      render: (value) => renderCount(value, 'neutral')
    },
    {
      title: 'Total Days',
      dataIndex: 'totalLeaveDays',
      key: 'totalLeaveDays',
      align: 'center',
      width: 105,
      render: (value) => <span className="num num--strong">{value}</span>,
      sorter: (a, b) => a.totalLeaveDays - b.totalLeaveDays
    },
    {
      title: 'Total Hours',
      dataIndex: 'totalLeaveHours',
      key: 'totalLeaveHours',
      align: 'center',
      width: 110,
      render: (value) => <span className="num">{value.toFixed(2)}</span>,
      sorter: (a, b) => a.totalLeaveHours - b.totalLeaveHours
    }
  ];

  const cards = [
    {
      title: 'Employees with leave',
      value: reportSummary.employeesWithLeave,
      icon: <TeamOutlined />,
      tone: 'primary'
    },
    {
      title: 'Sick leave',
      value: reportSummary.sickDays,
      icon: <MedicineBoxOutlined />,
      tone: 'warning',
      unit: 'days'
    },
    {
      title: 'Local leave',
      value: reportSummary.localLeaveDays,
      icon: <CalendarOutlined />,
      tone: 'primary',
      unit: 'days'
    },
    {
      title: 'Absent',
      value: reportSummary.absentDays,
      icon: <StopOutlined />,
      tone: 'danger',
      unit: 'days'
    },
    {
      title: 'Day off',
      value: reportSummary.dayOffDays,
      icon: <RestOutlined />,
      tone: 'neutral',
      unit: 'days'
    },
    {
      title: 'Leave hours',
      value: reportSummary.totalHours,
      icon: <ClockCircleOutlined />,
      tone: 'neutral',
      unit: 'h'
    }
  ];

  return (
    <Page>
      <PageHeader
        title="Leave & attendance report"
        subtitle="Leave, absences and days off per employee for any date range, ready to export."
        actions={
          <>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => fetchReportData()}
              loading={loading}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={handleExport}
              disabled={reportRows.length === 0}
            >
              Export XLSX
            </Button>
          </>
        }
      />

      <Card size="small">
        <div className="toolbar">
          <RangePicker
            format="DD/MM/YYYY"
            value={dateRange}
            onChange={handleDateRangeChange}
            allowClear={false}
          />
          <Select
            style={{ width: 280 }}
            value={employeeFilter}
            options={employeeOptions}
            onChange={setEmployeeFilter}
            showSearch
            optionFilterProp="label"
            placeholder="Select employee"
          />
          <Select
            style={{ width: 210 }}
            value={leaveTypeFilter}
            options={LEAVE_TYPE_OPTIONS}
            onChange={setLeaveTypeFilter}
          />
          <span className="toolbar__spacer" />
          {lastUpdated && (
            <Text type="secondary" style={{ fontSize: 12.5 }}>
              Updated {lastUpdated.format('D MMM YYYY, HH:mm')}
            </Text>
          )}
        </div>
      </Card>

      <StatGrid columns={6}>
        {cards.map((item) => (
          <StatCard
            key={item.title}
            label={item.title}
            value={item.value}
            unit={item.unit === 'days' && item.value === 1 ? 'day' : item.unit}
            icon={item.icon}
            tone={item.tone}
            loading={loading && !lastUpdated}
          />
        ))}
      </StatGrid>

      <Card
        className="table-card"
        title={
          <CardTitle
            title="Employee breakdown"
            subtitle={`${reportRows.length} ${reportRows.length === 1 ? 'employee' : 'employees'}${leaveTypeFilter !== 'all' ? ` · ${LEAVE_TYPE_LABELS[leaveTypeFilter] || leaveTypeFilter} only` : ''}`}
          />
        }
      >
        <Table
          rowKey="employeeId"
          loading={loading}
          columns={columns}
          dataSource={reportRows}
          scroll={{ x: 1200 }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ['15', '30', '50'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows`
          }}
          locale={{
            emptyText: (
              <EmptyState
                plain
                icon={<CalendarOutlined />}
                title="No leave records found"
                description="Nothing matches these filters. Try a wider date range or another leave type."
              />
            )
          }}
        />
      </Card>
    </Page>
  );
};

export default Reports;
